import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, HelpCircle, Moon, Sun, Gift, Users, Heart, Utensils } from 'lucide-react';

export default function RatgeberWasIstEid() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/ratgeber"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Alle Ratgeber
          </Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4">
            <Calendar className="w-4 h-4" />
            <span>Aktualisiert: Februar 2026</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">
            Was ist Eid? Ein Leitfaden für Freunde, Nachbarn und Kollegen
          </h1>
          <p className="text-lg text-white/80 leading-relaxed">
            Alles, was Sie über die islamischen Feiertage wissen sollten – einfach erklärt für 
            alle, die mehr über Eid erfahren möchten.
          </p>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          
          {/* Introduction */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-[#2C332E] leading-relaxed mb-8">
              Vielleicht haben Sie Kollegen, Nachbarn oder Freunde, die Eid feiern, und möchten mehr 
              darüber erfahren. Oder Sie wurden zu einem Eid-Fest eingeladen und fragen sich, was Sie 
              erwartet. Dieser Leitfaden erklärt Ihnen alles Wichtige über die islamischen Feiertage – 
              verständlich, respektvoll und informativ.
            </p>

            {/* Quick Overview */}
            <div className="bg-[#F5F5F0] rounded-2xl p-6 md:p-8 mb-12">
              <h2 className="font-serif text-xl font-medium text-[#2C332E] mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-[#4A5D4F]" />
                Kurz & Knapp: Das Wichtigste über Eid
              </h2>
              <ul className="space-y-3 text-[#4B5563]">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm">1</span>
                  <span><strong>Es gibt zwei Eid-Feste:</strong> Eid ul Fitr (nach dem Ramadan) und Eid ul Adha (Opferfest)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm">2</span>
                  <span><strong>"Eid Mubarak"</strong> bedeutet "Gesegnetes Fest" – der übliche Gruß</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm">3</span>
                  <span><strong>Familie steht im Mittelpunkt:</strong> Es wird zusammen gegessen, gebetet und gefeiert</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm">4</span>
                  <span><strong>Geschenke sind üblich:</strong> Besonders Kinder erhalten Geld oder kleine Präsente</span>
                </li>
              </ul>
            </div>

            {/* Table of Contents */}
            <div className="bg-[#F5F5F0] rounded-2xl p-6 md:p-8 mb-12">
              <h2 className="font-serif text-xl font-medium text-[#2C332E] mb-4">
                📖 Inhaltsverzeichnis
              </h2>
              <ul className="space-y-2 text-[#4A5D4F]">
                <li><a href="#was-bedeutet" className="hover:underline">1. Was bedeutet "Eid"?</a></li>
                <li><a href="#zwei-feste" className="hover:underline">2. Die zwei Eid-Feste erklärt</a></li>
                <li><a href="#wie-gefeiert" className="hover:underline">3. Wie wird Eid gefeiert?</a></li>
                <li><a href="#gruss" className="hover:underline">4. Wie gratuliert man zu Eid?</a></li>
                <li><a href="#eingeladen" className="hover:underline">5. Zu Eid eingeladen – was nun?</a></li>
                <li><a href="#geschenke" className="hover:underline">6. Muss ich ein Geschenk mitbringen?</a></li>
                <li><a href="#termine" className="hover:underline">7. Wann ist Eid 2026?</a></li>
                <li><a href="#fragen" className="hover:underline">8. Häufige Fragen</a></li>
              </ul>
            </div>

            {/* Section 1 */}
            <section id="was-bedeutet" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Moon className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  1. Was bedeutet "Eid"?
                </h2>
              </div>
              
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Das arabische Wort "Eid" (عيد) bedeutet schlicht "Fest" oder "Feiertag". Es wird 
                ähnlich ausgesprochen wie das deutsche Wort "Eid" (Schwur), hat aber eine völlig 
                andere Bedeutung.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Im Islam gibt es zwei Hauptfeste, die beide "Eid" heißen:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Eid ul Fitr</strong> (عيد الفطر) – "Fest des Fastenbrechens"</li>
                <li><strong>Eid ul Adha</strong> (عيد الأضحى) – "Fest der Opferung" oder "Opferfest"</li>
              </ul>
              <p className="text-[#4B5563] leading-relaxed">
                Beide Feste sind für Muslime weltweit von großer Bedeutung und werden mit Familie, 
                Freunden und der Gemeinschaft gefeiert. Sie können sie sich wie eine Mischung aus 
                Weihnachten und Thanksgiving vorstellen – Tage der Freude, des Zusammenkommens und 
                der Dankbarkeit.
              </p>
            </section>

            {/* Section 2 */}
            <section id="zwei-feste" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Sun className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  2. Die zwei Eid-Feste erklärt
                </h2>
              </div>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">
                Eid ul Fitr – Das Zuckerfest
              </h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Eid ul Fitr markiert das Ende des Ramadan, des islamischen Fastenmonats. Während 
                des Ramadan fasten Muslime von Sonnenaufgang bis Sonnenuntergang – sie essen und 
                trinken nichts in dieser Zeit.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Am Ende dieses intensiven Monats wird Eid ul Fitr gefeiert – eine Belohnung für 
                die Anstrengung des Fastens. In Deutschland wird es oft "Zuckerfest" genannt, 
                weil viele Süßigkeiten gegessen und verschenkt werden.
              </p>
              <div className="bg-[#F5F5F0] rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">Das Wichtigste über Eid ul Fitr:</h4>
                <ul className="text-[#4B5563] space-y-2">
                  <li>• Findet direkt nach dem Ramadan statt (1. Shawwal im islamischen Kalender)</li>
                  <li>• Dauert traditionell 3 Tage</li>
                  <li>• Süßigkeiten und festliches Essen stehen im Mittelpunkt</li>
                  <li>• Vor dem Fest wird "Zakat ul-Fitr" (Armenspende) gegeben</li>
                </ul>
              </div>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">
                Eid ul Adha – Das Opferfest
              </h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Eid ul Adha ist das "Fest der Opferung" und erinnert an eine Geschichte, die auch 
                in der Bibel vorkommt: Der Prophet Ibrahim (Abraham) war bereit, seinen Sohn auf 
                Gottes Befehl zu opfern. Als Zeichen seines Gehorsams sandte Gott einen Widder als 
                Ersatz.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Zu diesem Fest opfern viele muslimische Familien ein Tier (meist ein Schaf, eine 
                Ziege oder ein Rind). Das Fleisch wird in drei Teile geteilt: für die Familie, für 
                Verwandte/Freunde und für Bedürftige.
              </p>
              <div className="bg-[#F5F5F0] rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">Das Wichtigste über Eid ul Adha:</h4>
                <ul className="text-[#4B5563] space-y-2">
                  <li>• Findet ca. 70 Tage nach Eid ul Fitr statt</li>
                  <li>• Fällt zeitlich mit der Hadsch-Pilgerfahrt zusammen</li>
                  <li>• Dauert traditionell 4 Tage</li>
                  <li>• Das Teilen von Fleisch mit Bedürftigen ist zentral</li>
                  <li>• Gilt als das "größere" der beiden Eid-Feste</li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section id="wie-gefeiert" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  3. Wie wird Eid gefeiert?
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Der typische Ablauf eines Eid-Tages sieht ungefähr so aus:
              </p>

              <div className="space-y-4 mb-6">
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">1</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Morgens: Das Eid-Gebet</h4>
                    <p className="text-[#4B5563]">Muslime versammeln sich früh am Morgen in der Moschee oder auf einem offenen Platz zum gemeinsamen Gebet. Alle tragen ihre beste Kleidung – oft neu gekauft für diesen Anlass.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">2</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Nach dem Gebet: Gratulationen</h4>
                    <p className="text-[#4B5563]">Man umarmt sich, wünscht sich "Eid Mubarak" und gratuliert einander. Die Gemeinschaft und das Miteinander stehen im Vordergrund.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">3</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Tagsüber: Familienbesuche</h4>
                    <p className="text-[#4B5563]">Man besucht Verwandte, beginnend mit den Älteren (Großeltern, Eltern). Kinder erhalten Geschenke oder Geld ("Eidi"). Es wird viel gegessen und gelacht.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">4</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Den ganzen Tag: Festessen</h4>
                    <p className="text-[#4B5563]">Es werden besondere Gerichte gekocht – oft traditionelle Spezialitäten, die nur zu Eid zubereitet werden. Süßigkeiten und Desserts gibt es im Überfluss.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section id="gruss" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  4. Wie gratuliert man zu Eid?
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Der einfachste und universellste Gruß ist:
              </p>

              <div className="bg-[#4A5D4F] text-white rounded-xl p-6 text-center mb-6">
                <p className="text-3xl font-serif mb-2">Eid Mubarak!</p>
                <p className="text-white/70">(Aussprache: "Ied Mu-ba-rak")</p>
                <p className="text-sm mt-3">Bedeutet: "Gesegnetes Fest!" oder "Ein gesegnetes Eid!"</p>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Weitere Grüße, die Sie verwenden können:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>"Frohes Eid!"</strong> – Einfach und verständlich auf Deutsch</li>
                <li><strong>"Eid Mubarak, möge euer Fest gesegnet sein"</strong> – Etwas ausführlicher</li>
                <li><strong>"Ich wünsche dir/Ihnen ein schönes Eid-Fest"</strong> – Neutral und freundlich</li>
              </ul>

              <div className="bg-[#F5F5F0] rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">💡 Tipp</h4>
                <p className="text-[#4B5563]">
                  Muslime freuen sich sehr, wenn Nicht-Muslime ihnen zu Eid gratulieren! Es zeigt 
                  Interesse und Respekt. Keine Sorge, wenn Sie die Aussprache nicht perfekt hinbekommen – 
                  die Geste zählt.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section id="eingeladen" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Utensils className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  5. Zu Eid eingeladen – was nun?
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Eine Einladung zu einem Eid-Fest zu erhalten ist eine Ehre! Hier sind einige Tipps, 
                damit Sie sich wohl fühlen:
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Kleidung</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Eid ist ein festlicher Anlass. Ziehen Sie sich gepflegt an – ähnlich wie Sie es zu 
                einer Geburtstagsfeier oder einem Sonntagsessen tun würden. Sie müssen keine 
                traditionelle Kleidung tragen, aber vermeiden Sie zu freizügige Outfits aus Respekt.
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Essen</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Erwarten Sie viel und leckeres Essen! Muslime essen kein Schweinefleisch und 
                trinken keinen Alkohol. Das servierte Essen wird "Halal" sein (nach islamischen 
                Vorschriften erlaubt). Probieren Sie ruhig alles – es wird köstlich sein!
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Verhalten</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li>Schuhe werden oft am Eingang ausgezogen</li>
                <li>In manchen Familien sitzen Männer und Frauen getrennt</li>
                <li>Seien Sie offen und stellen Sie gerne Fragen – Gastgeber erklären gerne!</li>
                <li>Komplimentieren Sie das Essen und die Dekoration</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section id="geschenke" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  6. Muss ich ein Geschenk mitbringen?
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Ein Geschenk ist nicht zwingend erforderlich, aber eine nette Geste, die sehr 
                geschätzt wird. Hier sind einige passende Ideen:
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">✅ Gute Geschenkideen</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Blumen oder Pflanzen</li>
                    <li>• Hochwertige Schokolade</li>
                    <li>• Datteln (das klassische Eid-Geschenk)</li>
                    <li>• Tee oder Kaffee</li>
                    <li>• Selbstgebackenes</li>
                    <li>• Duftkerzen</li>
                  </ul>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">❌ Besser vermeiden</h4>
                  <ul className="text-sm text-[#4B5563] space-y-1">
                    <li>• Alkohol (Wein, Sekt, etc.)</li>
                    <li>• Produkte mit Schweinefleisch</li>
                    <li>• Nicht-Halal-Gummibärchen (Gelatine)</li>
                    <li>• Lederprodukte (wenn nicht Halal)</li>
                  </ul>
                </div>
              </div>

              <p className="text-[#4B5563] leading-relaxed">
                <strong>Tipp:</strong> Wenn Kinder im Haushalt leben, bringen Sie gerne etwas für 
                sie mit – ein kleines Spielzeug, Süßigkeiten oder einen Geldbetrag in einem hübschen 
                Umschlag. Kinder sind zu Eid die Ehrengäste!
              </p>
            </section>

            {/* Section 7 */}
            <section id="termine" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  7. Wann ist Eid 2026?
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-[#4A5D4F] text-white rounded-xl p-6">
                  <h3 className="font-serif text-xl font-medium mb-2">Eid ul Fitr 2026</h3>
                  <p className="text-2xl font-medium mb-2">20. März 2026</p>
                  <p className="text-white/70 text-sm">Nach dem Ramadan (Zuckerfest)</p>
                </div>
                <div className="bg-[#4A5D4F] text-white rounded-xl p-6">
                  <h3 className="font-serif text-xl font-medium mb-2">Eid ul Adha 2026</h3>
                  <p className="text-2xl font-medium mb-2">27. Mai 2026</p>
                  <p className="text-white/70 text-sm">Opferfest (größeres Eid)</p>
                </div>
              </div>

              <p className="text-[#4B5563] leading-relaxed">
                <strong>Hinweis:</strong> Das genaue Datum hängt von der Mondsichtung ab und kann 
                um einen Tag variieren. Muslime folgen dem islamischen Mondkalender, weshalb die 
                Termine jedes Jahr etwa 10-11 Tage früher fallen.
              </p>
            </section>

            {/* Section 8 - FAQ */}
            <section id="fragen" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <HelpCircle className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  8. Häufige Fragen
                </h2>
              </div>

              <div className="space-y-4">
                <div className="bg-[#F5F5F0] rounded-xl p-6">
                  <h4 className="font-medium text-[#2C332E] mb-2">Ist Eid ein Feiertag in Deutschland?</h4>
                  <p className="text-[#4B5563]">
                    Eid ist kein gesetzlicher Feiertag in Deutschland. Viele Muslime nehmen sich 
                    jedoch Urlaub oder bitten um einen freien Tag. In einigen Bundesländern können 
                    muslimische Schüler an Eid vom Unterricht befreit werden.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-6">
                  <h4 className="font-medium text-[#2C332E] mb-2">Warum ändern sich die Termine jedes Jahr?</h4>
                  <p className="text-[#4B5563]">
                    Der islamische Kalender ist ein Mondkalender mit etwa 354 Tagen pro Jahr. 
                    Deshalb verschieben sich die islamischen Feiertage jedes Jahr um ca. 10-11 Tage 
                    im Vergleich zum gregorianischen (westlichen) Kalender.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-6">
                  <h4 className="font-medium text-[#2C332E] mb-2">Kann ich als Nicht-Muslim an Eid teilnehmen?</h4>
                  <p className="text-[#4B5563]">
                    Ja, absolut! Muslime freuen sich, wenn Freunde und Nachbarn an ihrer Freude 
                    teilhaben. Wenn Sie eingeladen werden, nehmen Sie die Einladung gerne an. 
                    Es ist eine wunderbare Gelegenheit für interkulturellen Austausch.
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-6">
                  <h4 className="font-medium text-[#2C332E] mb-2">Was bedeutet "Bayram"?</h4>
                  <p className="text-[#4B5563]">
                    "Bayram" ist das türkische Wort für Fest und wird oft synonym mit Eid verwendet. 
                    "Ramazan Bayramı" = Eid ul Fitr, "Kurban Bayramı" = Eid ul Adha. Viele türkischstämmige 
                    Muslime in Deutschland verwenden diese Begriffe.
                  </p>
                </div>
              </div>
            </section>

            {/* Conclusion */}
            <section className="mb-12">
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-6">
                Fazit: Brücken bauen durch Verständnis
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Eid ist ein wunderschönes Fest der Freude, Familie und Großzügigkeit. Indem Sie 
                sich für die Traditionen Ihrer muslimischen Freunde, Nachbarn oder Kollegen 
                interessieren, bauen Sie Brücken und fördern ein harmonisches Miteinander.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Ein einfaches "Eid Mubarak" kann viel bewirken – es zeigt Respekt, Interesse und 
                Wertschätzung. Und wer weiß, vielleicht werden Sie das nächste Mal zu einem 
                unvergesslichen Eid-Fest eingeladen!
              </p>
              <p className="text-xl text-[#4A5D4F] font-medium">
                Eid Mubarak an alle, die feiern! 🌙
              </p>
            </section>

            {/* CTA */}
            <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
              <h3 className="font-serif text-2xl font-medium text-white mb-4">
                Auf der Suche nach Eid Geschenken?
              </h3>
              <p className="text-white/80 mb-6">
                Entdecken Sie unsere handverlesene Auswahl an Geschenkideen für Eid
              </p>
              <Link
                to="/geschenke"
                className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors"
              >
                Geschenke entdecken
                <Gift className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
